import { z } from 'zod'

/**
 * Default quick-access emojis for reactions (unified codes)
 * Users can customize these in their preferences
 */
export const DEFAULT_QUICK_EMOJIS = [
  '1F44D', // 👍 thumbsup
  '2764-FE0F',  // ❤️ red heart
  '1F389', // 🎉 tada/party popper
  '1F44F', // 👏 clap
  '1F440', // 👀 eyes
  '1F680', // 🚀 rocket
  '1F602', // 😂 laugh/joy
];

/**
 * Mapping of old enum values to emoji unified codes
 * Used for migration compatibility
 */
export const LEGACY_REACTION_TO_EMOJI: Record<string, string> = {
  'LIKE': '1F44D',
  'THUMBSUP': '1F44D',
  'LOVE': '2764',
  'HEART': '2764',
  'HAHA': '1F602',
  'LAUGH': '1F602',
  'WOW': '1F62E',
  'SAD': '1F622',
  'ANGRY': '1F621',
};

/**
 * @deprecated Use emoji strings instead. Kept for backward compatibility during migration.
 */
export enum ThreadReactionType {
  LIKE = 'LIKE',
  LOVE = 'LOVE',
  HAHA = 'HAHA',
  WOW = 'WOW',
  SAD = 'SAD',
  ANGRY = 'ANGRY'
}

/**
 * Convert emoji unified code to native emoji character
 * @param unified - Unified code like "1F44D" or "1F44D-1F3FB" (with skin tone)
 * @returns Native emoji string like "👍", or fallback emoji if invalid
 */
export function unifiedToNative(unified: string): string {
  if (!unified || typeof unified !== 'string') {
    return '❓'; // Fallback for missing/invalid emoji
  }

  try {
    const codePoints = unified.split('-').map(u => {
      const parsed = parseInt(u, 16);
      if (isNaN(parsed) || parsed < 0 || parsed > 0x10FFFF) {
        throw new Error(`Invalid code point: ${u}`);
      }
      return parsed;
    });
    return String.fromCodePoint(...codePoints);
  } catch {
    return '❓'; // Fallback for any parsing errors
  }
}

/**
 * Convert native emoji to unified code
 * @param native - Native emoji string like "👍"
 * @returns Unified code like "1F44D"
 */
export function nativeToUnified(native: string): string {
  const codePoints: string[] = [];
  for (const char of native) {
    const codePoint = char.codePointAt(0);
    if (codePoint) {
      codePoints.push(codePoint.toString(16).toUpperCase());
    }
  }
  return codePoints.join('-');
}

/**
 * Reaction group for displaying grouped emoji reactions
 * Similar to Slack's reaction display
 */
export interface ReactionGroup {
  /** Emoji unified code (e.g., "1F44D") */
  emoji: string;
  /** Native emoji character (e.g., "👍") */
  nativeEmoji: string;
  /** Total count of this reaction */
  count: number;
  /** Whether the current user has reacted with this emoji */
  hasReacted: boolean;
  /** Optional list of users who reacted (for tooltips) */
  reactors?: ReactionReactor[];
}

/**
 * User who reacted (for tooltips showing who reacted)
 */
export interface ReactionReactor {
  id: string;
  displayName: string;
  image?: string | null;
}

/**
 * Basic thread reaction summary (for backward compatibility)
 * @deprecated Use ReactionGroup[] for multi-emoji support
 */
export interface ThreadReactionSummary {
  id: string;
  reaction?: ThreadReactionType; // Legacy enum, optional for backward compatibility
  emoji?: string; // New emoji-based reaction (unified code)
}

// Thread Reaction schema - now uses emoji string instead of enum
export const ThreadReactionSchema = z.object({
  id: z.string().uuid(),
  threadId: z.string().uuid(),
  creatorId: z.string().uuid(),
  emoji: z.string().max(20), // Unified code like "1F44D"
  createdAt: z.date(),
  updatedAt: z.date(),
})

export type ThreadReaction = z.infer<typeof ThreadReactionSchema>

// Thread Comment Reaction schema - now uses emoji string instead of enum
export const ThreadCommentReactionSchema = z.object({
  id: z.string().uuid(),
  commentId: z.string().uuid(),
  creatorId: z.string().uuid(),
  emoji: z.string().max(20), // Unified code like "1F44D"
  createdAt: z.date(),
  updatedAt: z.date(),
})

export type ThreadCommentReaction = z.infer<typeof ThreadCommentReactionSchema>

/**
 * Helper to group raw reactions into ReactionGroup array
 */
export function groupReactionsByEmoji(
  reactions: Array<{ emoji: string; creatorId: string; creator?: ReactionReactor }>,
  currentUserId?: string
): ReactionGroup[] {
  const grouped = new Map<string, ReactionGroup>();

  for (const r of reactions) {
    if (!grouped.has(r.emoji)) {
      grouped.set(r.emoji, {
        emoji: r.emoji,
        nativeEmoji: unifiedToNative(r.emoji),
        count: 0,
        hasReacted: false,
        reactors: [],
      });
    }
    const group = grouped.get(r.emoji)!;
    group.count++;
    if (r.creator) {
      group.reactors?.push(r.creator);
    }
    if (r.creatorId === currentUserId) {
      group.hasReacted = true;
    }
  }

  return Array.from(grouped.values());
}
